import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, User, Phone, Camera, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getRegistrationSessionByToken } from '../../lib/services/registrationSession';
import { NotionTypingInput } from '../../components/ui/notion/NotionTypingInput';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { trackRegisterStepViewed } from '../../lib/analytics/onboardingEvents';
import { REGISTER_VISUALS } from '../../components/register/registerVisuals';
import { ProfileLegalConsent } from '../../components/register/ProfileLegalConsent';
import { TERMS_VERSION } from '../../lib/config/termsVersion';
import { useExitWarning } from '../../lib/hooks';

/**
 * Step 4 del flusso di registrazione: profilo personale (foto facoltativa,
 * nome, cognome, telefono, consenso marketing). Richiede la sessione creata
 * dall'auto-login dello step password (o dal ramo OAuth). Aggiorna direttamente
 * user_profiles via supabase-js (stesse colonne di ProfileEditModal).
 */
export function ProfileSetupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionToken = searchParams.get('token');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; phone?: string; generic?: string }>({});
  // T&C mai accettati (signup social partito da login/One Tap, senza le
  // spunte di /register): li raccogliamo qui, primo step visibile del wizard.
  const [needsTerms, setNeedsTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [vexatiousAccepted, setVexatiousAccepted] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  useExitWarning(true, 'Sei sicuro di voler abbandonare la registrazione?');

  useEffect(() => { trackRegisterStepViewed('profile'); }, []);

  // Guard: serve una sessione autenticata (auto-login post-password o OAuth).
  // Senza sessione si torna all'entry (il resume via token riparte da lì).
  useEffect(() => {
    let cancelled = false;
    if (!sessionToken) {
      navigate('/register');
      return;
    }
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session) {
        navigate('/register');
        return;
      }
      setUserId(session.user.id);
      // Prefill da user_profiles (ramo OAuth ha già full_name dal provider)
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name, phone, profile_image_url, terms_accepted_at')
        .eq('id', session.user.id)
        .single();
      if (cancelled) return;
      const fullName = (profile?.full_name || session.user.user_metadata?.full_name || '').trim();
      // Non prefillare col fallback email-prefix di register-password
      if (fullName && !session.user.email?.startsWith(fullName)) {
        const parts = fullName.split(/\s+/);
        setFirstName(parts[0] || '');
        setLastName(parts.slice(1).join(' '));
      }
      if (profile?.phone) setPhone(profile.phone);
      if (profile?.profile_image_url) setImageUrl(profile.profile_image_url);
      // T&C già accettati? Su user_profiles per il ramo OAuth da /register
      // (register-oauth-start le scrive subito); nella registration_session
      // per il ramo email (l'entry le salva in personal_data e register-complete
      // le copia sul profilo solo a fine wizard). Se mancano in entrambi
      // (signup social da login/One Tap) le chiediamo qui.
      let hasTerms = !!profile?.terms_accepted_at;
      if (!hasTerms) {
        const { data: regSession } = await getRegistrationSessionByToken(sessionToken);
        if (cancelled) return;
        const acceptance = (regSession?.personal_data as { terms_acceptance?: { accepted_at?: string } } | null)?.terms_acceptance;
        hasTerms = !!acceptance?.accepted_at;
      }
      setNeedsTerms(!hasTerms);
      setCheckingAuth(false);
    })();
    return () => { cancelled = true; };
  }, [navigate, sessionToken]);

  const handleImageUpload = async (file: File) => {
    if (!file || !userId) return;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, generic: 'Formato non supportato. Usa JPG o PNG.' }));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, generic: 'File troppo grande. Massimo 2MB.' }));
      return;
    }
    setUploadingImage(true);
    setErrors(prev => ({ ...prev, generic: undefined }));
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `profile_${userId}_${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('uploads')
        .upload(`profile/${fileName}`, file, { cacheControl: '3600', upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('uploads').getPublicUrl(`profile/${fileName}`);
      setImageUrl(publicUrl);
    } catch (e) {
      console.error('[ProfileSetup] upload error:', e);
      setErrors(prev => ({ ...prev, generic: "Impossibile caricare l'immagine" }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!firstName.trim()) next.firstName = 'Nome richiesto';
    if (!lastName.trim()) next.lastName = 'Cognome richiesto';
    if (!phone.trim()) next.phone = 'Telefono richiesto';
    setErrors(next);
    if (needsTerms && (!termsAccepted || !vexatiousAccepted)) {
      setTermsError('Per continuare devi accettare i Termini e approvare specificamente le clausole indicate');
      return;
    }
    if (Object.keys(next).length > 0 || !userId) return;

    setIsSubmitting(true);
    try {
      const acceptedAt = new Date().toISOString();
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const update: Record<string, unknown> = {
        full_name: fullName,
        phone: phone.trim(),
        profile_image_url: imageUrl,
        marketing_consent_email: marketingConsent,
        marketing_consent_date: marketingConsent ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
        ...(needsTerms ? { terms_accepted_at: acceptedAt } : {}),
      };
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update(update)
        .eq('id', userId);
      if (profileError) throw profileError;
      if (needsTerms) {
        // Dettaglio probatorio (colonne migration 20260715): best-effort,
        // stesso pattern di register-oauth-start.
        const { error: termsDetailError } = await supabase
          .from('user_profiles')
          .update({
            terms_version: TERMS_VERSION,
            terms_acceptance: {
              accepted_at: acceptedAt,
              version: TERMS_VERSION,
              vexatious_accepted: true,
              method: 'oauth_profile_step',
            },
          })
          .eq('id', userId);
        if (termsDetailError) {
          console.warn('[ProfileSetup] terms_version/terms_acceptance non salvati:', termsDetailError.message);
        }
      }
      // Allinea anche i metadata auth (usati da ProfileEditModal / header)
      await supabase.auth.updateUser({ data: { full_name: fullName, profile_image_url: imageUrl } });
      navigate(`/register/company?token=${sessionToken}`);
    } catch (err) {
      console.error('[ProfileSetup] save error:', err);
      setErrors(prev => ({ ...prev, generic: 'Errore durante il salvataggio. Riprova.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!sessionToken) {
    return null;
  }

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#787774]" />
      </div>
    );
  }

  return (
    <RegisterSplitLayout visual={REGISTER_VISUALS.profile} step={4}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">Presentati</h1>
        <p className="text-[#787774] text-sm">Come ti chiami e come possiamo contattarti</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Avatar */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImage}
            className="relative w-16 h-16 rounded-full bg-[rgba(55,53,47,0.06)] border border-[rgba(55,53,47,0.16)] flex items-center justify-center overflow-hidden hover:border-[#2383E2] transition-colors"
          >
            {imageUrl ? (
              <img src={imageUrl} alt="Foto profilo" className="w-full h-full object-cover" />
            ) : uploadingImage ? (
              <Loader2 className="w-5 h-5 animate-spin text-[#787774]" />
            ) : (
              <Camera className="w-5 h-5 text-[#787774]" />
            )}
          </button>
          <div>
            <p className="text-sm font-medium text-[#37352F]">Foto profilo</p>
            <p className="text-xs text-[#787774]">Facoltativa — JPG o PNG, max 2MB</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <NotionTypingInput label="Nome" value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholders={['Mario', 'Giulia']} icon={<User className="w-4 h-4" />}
            error={errors.firstName} required />
          <NotionTypingInput label="Cognome" value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholders={['Rossi', 'Bianchi']} icon={<User className="w-4 h-4" />}
            error={errors.lastName} required />
        </div>
        <NotionTypingInput label="Telefono" type="tel" value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholders={['+39 333 123 4567']} icon={<Phone className="w-4 h-4" />}
          error={errors.phone} required />

        {/* Consenso marketing (colonne esistenti user_profiles) */}
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" checked={marketingConsent}
            onChange={(e) => setMarketingConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-[rgba(55,53,47,0.3)] text-[#2383E2] focus:ring-[#2383E2] cursor-pointer" />
          <span className="text-xs text-[#787774] leading-relaxed">
            Voglio ricevere aggiornamenti sul prodotto e consigli via email (facoltativo)
          </span>
        </label>

        {needsTerms && (
          <ProfileLegalConsent
            termsAccepted={termsAccepted}
            vexatiousAccepted={vexatiousAccepted}
            onTermsChange={(v) => { setTermsAccepted(v); if (v) setTermsError(null); }}
            onVexatiousChange={(v) => { setVexatiousAccepted(v); if (v) setTermsError(null); }}
            error={termsError}
          />
        )}

        {errors.generic && <p className="text-xs text-red-600">{errors.generic}</p>}

        <div className="pt-4">
          <button type="submit" disabled={isSubmitting || uploadingImage} className="notion-register-btn w-full">
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Salvataggio...</span>
              </>
            ) : (
              <>
                <span>Continua</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </RegisterSplitLayout>
  );
}
